import { useEffect, useRef, useState, type ChangeEvent, type FormEvent, type MouseEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { messageFromError } from '../../../api/client'
import { loadJourneyContent, renderTemplate, type JourneyContent, type JourneyStageDef } from '../../../entities/journey'
import { useAuthStore } from '../../../store/authStore'
import { chatDraftState } from '../../chat-draft'
import { journeyApi, type JourneyStateView } from '../api/journeyApi'
import type { JourneyAction, JourneyView } from './types'

// «Путь» screen state: the stage timeline and the tasks share one load of
// content + state and one source of truth (boards ccd73u7xtwif6iczp76zd3xjb5r
// merged «Следующий шаг» into «Путь»). Every mutation applies the fresh state
// view the API returns, so the selector, tasks and transition never diverge.
export function useJourney() {
  const userId = useAuthStore(s => s.userId)
  const generation = useAuthStore(s => s.sessionGeneration)
  const navigate = useNavigate()
  const [content, setContent] = useState<JourneyContent | null>(null)
  const [state, setState] = useState<JourneyStateView | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [revision, setRevision] = useState(0)
  const [editing, setEditing] = useState(false)
  const [selection, setSelection] = useState('')
  const [saving, setSaving] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const [confirmBreakup, setConfirmBreakup] = useState(false)
  const active = useRef(false)
  const pending = useRef(false)
  const stageRef = useRef<HTMLHeadingElement>(null)
  const editButtonRef = useRef<HTMLButtonElement>(null)
  const focusAfterEdit = useRef<HTMLElement | null>(null)
  useEffect(() => {
    if (editing || saving) return
    focusAfterEdit.current?.focus()
    focusAfterEdit.current = null
  }, [editing, saving])
  useEffect(() => {
    let cancelled = false
    active.current = true
    pending.current = false
    setLoading(true)
    setError('')
    setState(null)
    setContent(null)
    setEditing(false)
    setDismissed(false)
    setConfirmBreakup(false)
    Promise.all([loadJourneyContent(), journeyApi.state()]).then(([loaded, view]) => {
      if (cancelled) return
      if (view.stage && view.stage.total !== loaded.stages.length) {
        throw new Error('Путь на сервере не совпадает с контентом')
      }
      setContent(loaded)
      setState(view)
    }).catch(() => {
      if (!cancelled) setError('Не удалось загрузить путь. Попробуйте ещё раз.')
    }).finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true; active.current = false }
  }, [userId, generation, revision])

  const stages: JourneyStageDef[] = content?.stages ?? []
  const currentStage = state?.stage ? stages.find(stage => stage.id === state.stage?.id) ?? null : null
  const stageNo = state?.stage?.no ?? null
  const weeklyDone = (state?.weekly.length ?? 0) > 0 && state!.weekly.every(task => task.done)
  const view: JourneyView | null = content && state ? {
    state,
    content,
    stageNo,
    total: content.stages.length,
    status: state.finished?.kind === 'kept' ? 'relationship_saved'
      : stageNo === content.stages.length && weeklyDone ? 'completed'
        : 'active',
    focus: state.stage?.focus ?? null,
    today: state.daily ? { ...state.daily.task, done: state.daily.status === 'done' } : null,
    weekly: state.weekly,
    forkOptions: state.weekly.find(task => task.fork)?.fork?.options ?? [],
    ready: weeklyDone && (state.pending_next != null || stageNo === content.stages.length),
    extensionUsed: state.extended != null,
  } : null
  const doneCount = view?.weekly.filter(task => task.done).length ?? 0

  const current = () => active.current && useAuthStore.getState().userId === userId && useAuthStore.getState().sessionGeneration === generation
  const retry = () => setRevision(value => value + 1)

  const toggleEditing = () => {
    if (editing) focusAfterEdit.current = editButtonRef.current
    setSelection(currentStage?.id ?? '')
    setError('')
    setEditing(value => !value)
  }
  const selectStage = (event: ChangeEvent<HTMLSelectElement>) => setSelection(event.target.value)
  const saveStage = async (event: FormEvent) => {
    event.preventDefault()
    if (saving || !selection) return
    setSaving(true)
    setError('')
    try {
      const next = await journeyApi.setStage(selection)
      if (!current()) return
      focusAfterEdit.current = stageRef.current
      setState(next)
      setEditing(false)
      setDismissed(false)
      setConfirmBreakup(false)
    } catch {
      if (current()) setError('Не удалось сохранить стадию. Попробуйте ещё раз.')
    } finally {
      if (current()) setSaving(false)
    }
  }

  const send = async (action: JourneyAction) => {
    if (!stageNo || pending.current) return
    pending.current = true
    setSaving(true)
    setError('')
    try {
      if (action.action === 'complete' || action.action === 'week') {
        setState(await journeyApi.task(action.task_id, action.done))
      } else if (action.action === 'stay') {
        // Stay journals the look but changes no state — keep the loaded view
        // and hide the transition until the person reopens it.
        await journeyApi.stay(state!.stage!.id)
        setDismissed(true)
        return
      } else if (action.action === 'review') {
        setDismissed(false)
        return
      } else if (action.action === 'advance') {
        if (!state?.pending_next) throw new Error('Нет следующей стадии')
        setState(await journeyApi.setStage(state.pending_next.id))
        setDismissed(false)
      } else if (action.action === 'preserve') {
        setState(await journeyApi.fork('keep'))
      } else if (action.action === 'extend') {
        setState(await journeyApi.fork('extend'))
      } else if (action.action === 'split') {
        setState(await journeyApi.fork('split'))
      }
    } catch (err) {
      if (current()) setError(messageFromError(err, 'Не удалось сохранить задания. Попробуйте ещё раз.'))
    } finally {
      if (current()) { pending.current = false; setSaving(false) }
    }
  }
  const completeToday = (event: MouseEvent<HTMLButtonElement>) => { if (view?.today) void send({ action: 'complete', task_id: event.currentTarget.value, done: true }) }
  const markWeek = (event: ChangeEvent<HTMLInputElement>) => { void send({ action: 'week', task_id: event.target.value, done: event.target.checked }) }
  const resolveTransition = (event: MouseEvent<HTMLButtonElement>) => {
    const action = event.currentTarget.value
    if (action === 'stay' || action === 'review' || action === 'advance' || action === 'preserve' || action === 'extend' || action === 'split') {
      void send({ action })
    }
  }
  const beginBreakup = () => setConfirmBreakup(true)
  const cancelBreakup = () => setConfirmBreakup(false)

  const discussStage = () => {
    const template = content?.ui.discuss_stage_draft
    const text = currentStage && template
      ? renderTemplate(template, { stage: currentStage.title })
      : 'Хочу разобраться, на каком этапе отношений я сейчас нахожусь. Помоги мне в этом.'
    navigate('/', { state: chatDraftState(text) })
  }
  const discussTask = (event: MouseEvent<HTMLButtonElement>) => {
    const task = [view?.today, ...view?.weekly ?? []].find(item => item?.id === event.currentTarget.value)
    if (!task || !content) return
    const template = content.ui.discuss_task_draft
    const text = template ? renderTemplate(template, { task: task.title }) : `Хочу обсудить задание «${task.title}»: как оно у меня идёт.`
    navigate('/', { state: chatDraftState(text) })
  }
  return { stages, currentStage, view, doneCount, loading, error, editing, selection, saving, dismissed, confirmBreakup, stageRef, editButtonRef, retry, toggleEditing, selectStage, saveStage, discussStage, completeToday, markWeek, resolveTransition, beginBreakup, cancelBreakup, discussTask }
}
